import "./calendar.css";
import type { Place } from "@confluence/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState, type KeyboardEvent } from "react";
import { get } from "../api";
import { useI18n } from "../i18n";
import { useMe } from "../session";

// Suggestions de lieux (OpenStreetMap, via l'API) classées par proximité : position actuelle
// si le navigateur la donne, sinon la ville par défaut du compte.

const POSITION_KEY = "confluence-position";
const POSITION_TTL = 6 * 60 * 60_000;
type Coords = { lat: number; lon: number };

function cachedPosition(): Coords | null {
  try {
    const v = JSON.parse(localStorage.getItem(POSITION_KEY) ?? "null");
    return v && Date.now() - v.at < POSITION_TTL ? { lat: v.lat, lon: v.lon } : null;
  } catch {
    return null;
  }
}

/** Position actuelle, demandée une fois (le navigateur affiche sa propre autorisation). */
function usePosition(active: boolean): Coords | null {
  const [pos, setPos] = useState(cachedPosition);
  useEffect(() => {
    if (!active || pos || !("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c = { lat: p.coords.latitude, lon: p.coords.longitude };
        setPos(c);
        try {
          localStorage.setItem(POSITION_KEY, JSON.stringify({ ...c, at: Date.now() }));
        } catch {
          // stockage indisponible : la position sera redemandée
        }
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: POSITION_TTL, timeout: 8000 },
    );
  }, [active, pos]);
  return pos;
}

export function PlaceField({
  label,
  value,
  onChange,
  onPick,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onPick?: (p: Place) => void;
  placeholder?: string;
}) {
  const { t, locale } = useI18n();
  const me = useMe();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const position = usePosition(touched);
  const bias = position ?? me.data?.home ?? null;
  useEffect(() => {
    const h = setTimeout(() => setQuery(value.trim()), 300);
    return () => clearTimeout(h);
  }, [value]);
  const places = useQuery({
    queryKey: ["places", query, locale, bias?.lat.toFixed(1), bias?.lon.toFixed(1)],
    queryFn: async () => {
      const params = new URLSearchParams({ q: query, lang: locale, ...(bias ? { lat: String(bias.lat), lon: String(bias.lon) } : {}) });
      return (await get<{ places: Place[] }>(`/api/places?${params}`)).places;
    },
    enabled: open && query.length >= 3,
    staleTime: 5 * 60_000,
  });
  const list = open && query.length >= 3 ? (places.data ?? []) : [];

  const pick = (p: Place) => {
    onChange(p.address ? `${p.name}, ${p.address}` : p.name);
    onPick?.(p);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!list.length) return;
    if (e.key === "ArrowDown") setActive((a) => (a + 1) % list.length);
    else if (e.key === "ArrowUp") setActive((a) => (a - 1 + list.length) % list.length);
    else if (e.key === "Enter") pick(list[active]!);
    else if (e.key === "Escape") setOpen(false);
    else return;
    e.preventDefault();
  };

  return (
    <div className="field combo">
      <label htmlFor={`${id}-place`}>{label}</label>
      <input
        id={`${id}-place`}
        className="input"
        value={value}
        placeholder={placeholder ?? t("ev.placePlaceholder")}
        autoComplete="off"
        role="combobox"
        aria-expanded={list.length > 0}
        aria-controls={`${id}-list`}
        aria-activedescendant={list.length ? `${id}-o${active}` : undefined}
        maxLength={1000}
        onFocus={() => setTouched(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {list.length > 0 && (
        <ul className="poi" id={`${id}-list`} role="listbox">
          {list.map((p, i) => (
            <li
              key={`${p.name}-${p.lat}-${p.lon}`}
              id={`${id}-o${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
            >
              <span className="pi" aria-hidden="true">
                <svg className="icon" viewBox="0 0 24 24">
                  <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                  <circle cx="12" cy="9.5" r="2.5" />
                </svg>
              </span>
              <span className="pm">
                <span className="pn">{p.name}</span>
                <span className="pa">{p.address}</span>
              </span>
              {p.kind && <span className="pd">{p.kind}</span>}
            </li>
          ))}
          <li className="src" role="presentation">
            {t("ev.placeAttribution")}
          </li>
        </ul>
      )}
    </div>
  );
}
